/**
 * Generic OIDC provider wrapper.
 *
 * Sources configuration from the consolidated runtime config (config/index.ts) rather than
 * reading import.meta.env directly, so the same code path serves runtime-injected (deployed)
 * and vite-dev configuration (research R1, R7). Provider selection is driven by authMode.
 */

import type { OidcProviderConfig, AuthProviderType, SignoutRedirectArgs } from './types';
import { getConfig } from '../../config';

/**
 * The active auth provider. Real OIDC uses Cognito claim mapping; mock uses 'local'.
 */
export function getAuthProvider(): AuthProviderType {
  try {
    return getConfig().authMode === 'oidc' ? 'cognito' : 'local';
  } catch {
    return 'local';
  }
}

/**
 * Build the provider-agnostic OIDC configuration for the current environment.
 *
 * @returns OIDC configuration, or null in mock/local mode.
 */
export function buildOidcConfig(): OidcProviderConfig | null {
  const config = getConfig();
  if (config.authMode !== 'oidc' || !config.oidc) {
    return null;
  }
  const oidc = config.oidc;
  return {
    authority: oidc.authority,
    clientId: oidc.clientId,
    redirectUri: oidc.redirectUri,
    postLogoutRedirectUri: oidc.postLogoutRedirectUri,
    scope: oidc.scope,
    responseType: 'code',
  };
}

/** Whether mock/local authentication should be used. */
export function isMockAuthEnabled(): boolean {
  try {
    return getConfig().authMode === 'mock';
  } catch {
    return true;
  }
}

/**
 * Build provider-specific logout arguments.
 *
 * Different OIDC providers have different logout URL requirements:
 * - AWS Cognito: requires client_id + logout_uri query parameters (handled in useProviderLogout).
 * - Standard OIDC: uses id_token_hint + post_logout_redirect_uri.
 */
export function buildLogoutArgs(
  provider: AuthProviderType,
  clientId: string,
  postLogoutRedirectUri: string,
  idTokenHint?: string
): SignoutRedirectArgs {
  switch (provider) {
    case 'cognito':
      return {
        extraQueryParams: {
          client_id: clientId,
          logout_uri: postLogoutRedirectUri,
        },
      };
    default:
      return {
        id_token_hint: idTokenHint,
        post_logout_redirect_uri: postLogoutRedirectUri,
      };
  }
}
